import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { environment } from '../../../../environments/environment';
import { 
  PicklistType, 
  PicklistItem, 
  ListPicklistTypesResponse, 
  ListPicklistItemsResponse,
  PicklistCache 
} from '../models/picklist.model';

@Injectable({
  providedIn: 'root'
})
export class PicklistService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly baseUrl = environment.userManagementApiUrl;

  private picklistTypeEndpoint(kbId: number): string {
    return `${this.baseUrl}/knowledge-base/${kbId}/picklist-type`;
  }

  // Cache en memoria
  private picklistCache: PicklistCache = {};
  private cacheLoaded = false;
  private cachedKbId: number | null = null;

  /**
   * Cargar todos los picklists al cache (llamar al inicio de la app)
   */
  loadAllPicklists(kbId: number): Observable<boolean> {
    if (this.cacheLoaded && this.cachedKbId === kbId) {
      return of(true);
    }

    // Different KB or not yet loaded — reset cache
    this.picklistCache = {};
    this.cacheLoaded = false;
    this.cachedKbId = null;

    const headers = this.authService.getAuthHeaders();
    const endpoint = this.picklistTypeEndpoint(kbId);

    return this.http.get<ListPicklistTypesResponse>(endpoint, { headers }).pipe(
      switchMap(response => {
        const types = response.picklistTypes;
        
        if (!types || types.length === 0) {
          this.cacheLoaded = true;
          return of([]);
        }

        const itemRequests = types.map(type => 
          this.http.get<ListPicklistItemsResponse>(
            `${endpoint}/${type.picklistTypeId}/items`, 
            { headers }
          ).pipe(
            map(itemResponse => itemResponse.items || (itemResponse as any).picklistItems || []),
            catchError(error => {
              console.error(`Error loading items for type ${type.picklistTypeId}:`, error);
              return of([]);
            })
          )
        );
        
        return forkJoin(itemRequests);
      }),
      map(itemsArrays => {
        const allItems = itemsArrays.flat();
        
        this.picklistCache = {};
        allItems.forEach(item => {
          if (item && item.picklistItemId && item.name) {
            this.picklistCache[item.picklistItemId] = item.name;
          }
        });

        this.cacheLoaded = true;
        this.cachedKbId = kbId;
        return true;
      }),
      catchError(error => {
        console.error('PicklistService: Error loading picklists', error);
        return of(false);
      })
    );
  }

  /**
   * Obtener el nombre de un picklist item por su ID
   */
  getItemName(picklistItemId: number | null | undefined): string {
    if (!picklistItemId) return '-';
    
    return this.picklistCache[picklistItemId] || `Unknown (${picklistItemId})`;
  }

  /**
   * Limpiar cache (útil para testing o refresh)
   */
  clearCache(): void {
    this.picklistCache = {};
    this.cacheLoaded = false;
    this.cachedKbId = null;
  }

  /**
   * Obtener tipos de picklist
   */
  getPicklistTypes(kbId: number): Observable<PicklistType[]> {
    const headers = this.authService.getAuthHeaders();
    
    return this.http.get<ListPicklistTypesResponse>(this.picklistTypeEndpoint(kbId), { headers }).pipe(
      map(response => response.picklistTypes),
      catchError(error => {
        console.error('PicklistService: Error fetching picklist types', error);
        return of([]);
      })
    );
  }

  /**
   * Obtener items de un tipo específico
   */
  getPicklistItems(kbId: number, typeId: number, activeOnly = true): Observable<PicklistItem[]> {
    const headers = this.authService.getAuthHeaders();
    const url = `${this.picklistTypeEndpoint(kbId)}/${typeId}/items${activeOnly ? '?activeOnly=true' : ''}`;
    
    return this.http.get<ListPicklistItemsResponse>(url, { headers }).pipe(
      map(response => response.items || (response as any).picklistItems || []),
      catchError(error => {
        console.error(`PicklistService: Error fetching items for type ${typeId}`, error);
        return of([]);
      })
    );
  }

  /**
   * Crear un nuevo item de picklist
   */
  createPicklistItem(
    kbId: number,
    typeId: number,
    item: { code: string; name: string; value?: string | null; isActive: boolean }
  ): Observable<any> {
    const headers = this.authService.getAuthHeaders();
    
    return this.http.post(
      `${this.picklistTypeEndpoint(kbId)}/${typeId}/item`, item, { headers }
    ).pipe(
      tap(() => this.clearCache()),
      catchError(error => {
        console.error(`PicklistService: Error creating item for type ${typeId}`, error);
        throw error;
      })
    );
  }

  /**
   * Actualizar un item existente de picklist
   */
  updatePicklistItem(
    kbId: number,
    typeId: number,
    itemId: number,
    item: { name: string; isActive: boolean }
  ): Observable<any> {
    const headers = this.authService.getAuthHeaders();
    
    return this.http.put(
      `${this.picklistTypeEndpoint(kbId)}/${typeId}/item/${itemId}`, 
      item,
      { headers }
    ).pipe(
      tap(() => this.clearCache()),
      catchError(error => {
        console.error(`PicklistService: Error updating item ${itemId} for type ${typeId}`, error);
        throw error;
      })
    );
  }
}